import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MAT_PALETTE } from "../../styles/themes/mat-palette";
import { MatFigure } from "./MatFigure";

export type MatTeaserBadgeProps = {
    /** Localized target URL (already resolved by the caller via useLocalizedUrl). */
    href: string;
    /** Speech-bubble question, e.g. "Hoi! Welcher KI-Ansatz passt zu Ihrem Fall?". */
    question: string;
    /** CTA text without the arrow, e.g. "Mat fragen". */
    ctaLabel: string;
    /** Accessible name for the whole badge link. */
    ariaLabel: string;
    /**
     * Optional eyecatcher label rendered as a corner "Störer" badge (e.g.
     * "KI-Tipp"). When present, the teaser switches to its loud, pulsing
     * variant to grab attention; when omitted it stays calm.
     */
    badgeLabel?: string;
};

// KI-Technologie colour scheme (shared with MatFigure's onDark mode): a deep
// purple surface carrying the cyan stroke/glow. Reused here so the homepage
// teaser matches the AI-technology page's Mat instead of the light violet card.
const KI_SURFACE = MAT_PALETTE.visor; // deep purple (matches MatFigure onDark visor)
const KI_ACCENT = MAT_PALETTE.mint; // cyan stroke/glow (matches MatFigure onDark stroke)

/**
 * Schwebendes Header-Badge: winkender «Mat» mit Sprechblase, das zur
 * KI-Beratung einlädt. Ersetzt das frühere Showroom-HeroBadge an gleicher
 * Position (oben rechts über dem Hero-Bild). Alle Texte kommen von aussen
 * (i18n), damit keine harten Strings in der Komponente stehen.
 *
 * Farbschema übernommen von der KI-Technologie-Seite (Cyan #8ce6d1 auf dunklem
 * Deep-Purple, MatFigure onDark). Mit `badgeLabel` wird der Teaser zum
 * auffälligen «Störer»: dauerhafter Puls, Cyan-Glow, Eckbadge und grössere
 * Sprechblase, damit er nicht übersehen wird.
 */
export const MatTeaserBadge: React.FC<MatTeaserBadgeProps> = ({ href, question, ctaLabel, ariaLabel, badgeLabel }) => {
    const navigate = useNavigate();
    return (
        <Link
            to={href}
            aria-label={ariaLabel}
            className={[
                "absolute -top-8 right-1 md:-top-12 md:right-4 z-10",
                "flex flex-col items-center",
                "w-[210px] md:w-[240px] lg:w-[265px] xl:w-[285px]",
                "transform transition-transform duration-300 hover:scale-110",
                badgeLabel ? "animate-mat-stoerer-pulse" : "",
            ].join(" ")}
            onClick={e => {
                // Klick auf Mat (das SVG): Sprung-dann-navigieren übernimmt MatFigure.
                // Wir unterdrücken hier die sofortige Link-Navigation.
                if ((e.target as Element).closest?.("[data-testid=mat-figure]")) {
                    e.preventDefault();
                }
            }}
        >
            {/* ⚠️ **Die Sprechblase entfaellt unter `md`** (Entscheid Jan,
                14.9.2026). Auf 390 px ist der Hero 749 px hoch, der Text endet
                bei 669 px — es bleiben 80 px, und Mat misst mit Blase 254 px. Er
                muesste auf 31 % schrumpfen, um hineinzupassen; jede andere
                Position verdeckt Hero-Text oder ragt in die naechste Sektion.
                Ohne Blase bleiben 155 px, und die Figur traegt den Einstieg
                allein.

                `hidden md:block` und NICHT `opacity`: Eine unsichtbare Blase
                belegte weiter ihre 100 px und finge Klicks ab. Der Pfeil sitzt
                innerhalb und verschwindet mit ihr. */}
            <div
                className="animate-mat-bubble-in relative hidden w-full rounded-2xl border-2 px-4 py-3 shadow-2xl md:block"
                style={{
                    borderColor: KI_ACCENT,
                    backgroundColor: KI_SURFACE,
                    // Cyan halo lifts the card off the light hero — the "Störer" glow.
                    boxShadow: `0 0 0 1px ${KI_ACCENT}55, 0 10px 30px ${KI_ACCENT}66, 0 4px 14px rgba(0,0,0,0.35)`,
                }}
            >
                {/* Corner eyecatcher badge — only when content supplies a label. */}
                {badgeLabel ? (
                    <span
                        className={[
                            "absolute -right-2 -top-3 rounded-full px-2.5 py-0.5",
                            "text-[10px] font-extrabold uppercase tracking-wide",
                            "rotate-6 shadow-lg",
                        ].join(" ")}
                        style={{ backgroundColor: KI_ACCENT, color: KI_SURFACE }}
                    >
                        {badgeLabel}
                    </span>
                ) : null}
                <span className="block text-sm font-semibold leading-tight text-white md:text-base">{question}</span>
                <span
                    className="mt-2 inline-block rounded-full px-3 py-1 text-xs font-bold"
                    style={{ backgroundColor: KI_ACCENT, color: KI_SURFACE }}
                >
                    {ctaLabel} →
                </span>
                {/* tail pointing down at Mat — matches the dark card surface */}
                <span
                    className="absolute -bottom-2 left-6 h-0 w-0 border-x-8 border-t-8 border-x-transparent"
                    style={{ borderTopColor: KI_SURFACE }}
                />
            </div>
            <MatFigure onDark className="mt-[-4px] h-[155px] w-auto" onActivate={() => navigate(href)} />
        </Link>
    );
};

export default MatTeaserBadge;
